import { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Button } from "../../shared";
import {
  findP2pRecipient,
  makeP2pTransfer,
  P2pRecipient,
} from "../../../services/personal";
import { getUserInfo } from "../../../services/me";
import { GetUserInfoResponse } from "../../../services/me/interface";

interface Props {
  open: boolean;
  onClose: () => void;
}

type Step = "recipient" | "amount" | "pin" | "done";

/**
 * Основной drawer перевода: 3 шага + экран успеха.
 *   1. recipient — юзер вводит email/телефон, жмёт «Найти».
 *      При успехе показываем карточку получателя и кнопку «Далее».
 *   2. amount — выбор валюты из своих балансов и суммы.
 *   3. pin — ввод PIN → отправка запроса.
 *   4. done — экран успеха с суммой и получателем.
 */
export function P2pTransferDrawer({ open, onClose }: Props) {
  const { t } = useTranslation(["p2p", "profile", "errors"]);
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: me } = useQuery<GetUserInfoResponse>(
    ["user-info"],
    getUserInfo,
    { staleTime: 30_000, enabled: open },
  );

  const [step, setStep] = useState<Step>("recipient");
  const [query, setQuery] = useState("");
  const [recipient, setRecipient] = useState<P2pRecipient | null>(null);
  const [currency, setCurrency] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");
  const [pin, setPin] = useState("");

  useEffect(() => {
    if (!open) {
      setStep("recipient");
      setQuery("");
      setRecipient(null);
      setCurrency("");
      setAmount("");
      setComment("");
      setPin("");
    }
  }, [open]);

  useEffect(() => {
    // Первый ненулевой баланс — дефолтная валюта.
    if (open && me && !currency) {
      const first = me.balances?.find((b) => Number(b.amount) > 0);
      if (first) setCurrency(first.currency);
      else if (me.balances?.[0]) setCurrency(me.balances[0].currency);
    }
  }, [open, me, currency]);

  const findMut = useMutation({
    mutationFn: (q: string) => findP2pRecipient(q),
    onSuccess(data) {
      if (!data) {
        enqueueSnackbar(
          t("p2p:recipient_not_found", {
            defaultValue: "Пользователь не найден",
          }),
          { variant: "warning" },
        );
        return;
      }
      setRecipient(data);
    },
    onError(err) {
      enqueueSnackbar(pickMessage(err), { variant: "error" });
    },
  });

  const transferMut = useMutation({
    mutationFn: () =>
      makeP2pTransfer({
        recipientId: recipient!.id,
        currency,
        amount,
        comment: comment.trim() || undefined,
        pin,
      }),
    onSuccess() {
      qc.invalidateQueries(["user-info"]);
      qc.invalidateQueries(["p2p-history"]);
      setStep("done");
    },
    onError(err) {
      enqueueSnackbar(pickMessage(err), { variant: "error" });
    },
  });

  const currentBalance = me?.balances?.find((b) => b.currency === currency);
  const amountNum = Number(amount || "0");
  const balanceNum = Number(currentBalance?.amount || "0");
  const isAmountValid = amountNum > 0 && amountNum <= balanceNum;
  const isPinValid = /^\d{4}$/.test(pin);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100015 }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "100%",
          bgcolor: "background.default",
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          padding:
            "calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 20px)",
          boxSizing: "border-box",
        },
      }}
    >
      <Header
        title={
          step === "recipient"
            ? t("p2p:step_recipient_title", {
                defaultValue: "Кому переводим",
              })
            : step === "amount"
              ? t("p2p:step_amount_title", { defaultValue: "Сумма" })
              : step === "pin"
                ? t("p2p:step_pin_title", { defaultValue: "Подтверждение" })
                : t("p2p:step_done_title", { defaultValue: "Готово" })
        }
        onBack={
          step === "recipient"
            ? onClose
            : step === "amount"
              ? () => setStep("recipient")
              : step === "pin"
                ? () => setStep("amount")
                : onClose
        }
      />

      {/* ---- Шаг 1: получатель ---- */}
      {step === "recipient" && (
        <>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginBottom: "16px",
              lineHeight: 1.5,
            }}
          >
            {t("p2p:recipient_hint", {
              defaultValue: "Найдите пользователя по e-mail или номеру телефона.",
            })}
          </Typography>

          <TextField
            autoFocus
            fullWidth
            placeholder="user@mail.com или +79001234567"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setRecipient(null);
            }}
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
              },
            }}
          />

          {recipient && (
            <Box
              sx={(theme) => ({
                marginTop: "16px",
                padding: "16px",
                borderRadius: "16px",
                bgcolor: "secondary.main",
                border:
                  theme.palette.mode === "light"
                    ? "1px solid rgba(0,0,0,0.06)"
                    : "1px solid rgba(255,255,255,0.06)",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              })}
            >
              <Box
                component="img"
                src={recipient.avatar_url ?? undefined}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  bgcolor: "rgba(0,0,0,0.05)",
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {recipient.display_name}
                </Typography>
                <Typography
                  sx={{ fontSize: "12px", color: "text.secondary" }}
                >
                  {recipient.masked_contact}
                </Typography>
              </Box>
              <Icon
                icon="solar:check-circle-bold"
                width={22}
                height={22}
                color="#2ecc71"
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginTop: "20px",
            }}
          >
            {!recipient ? (
              <Button
                onClick={() => findMut.mutate(query.trim())}
                disabled={!query.trim() || findMut.isLoading}
              >
                {findMut.isLoading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  t("p2p:find", { defaultValue: "Найти" })
                )}
              </Button>
            ) : (
              <Button onClick={() => setStep("amount")}>
                {t("p2p:next", { defaultValue: "Далее" })}
              </Button>
            )}
          </Box>
        </>
      )}

      {/* ---- Шаг 2: сумма ---- */}
      {step === "amount" && recipient && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "12px 14px",
              borderRadius: "12px",
              bgcolor: "secondary.main",
              marginBottom: "20px",
            }}
          >
            <Box
              component="img"
              src={recipient.avatar_url ?? undefined}
              alt=""
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "rgba(0,0,0,0.05)",
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "13px", color: "text.secondary" }}
              >
                {t("p2p:recipient_label", { defaultValue: "Получатель" })}
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "text.primary",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {recipient.display_name}
              </Typography>
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginBottom: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t("p2p:currency", { defaultValue: "Валюта" })}
          </Typography>
          <Select
            fullWidth
            value={currency}
            onChange={(e) => setCurrency(e.target.value as string)}
            sx={{
              borderRadius: "14px",
              bgcolor: "secondary.main",
              marginBottom: "16px",
            }}
          >
            {(me?.balances ?? []).map((b) => (
              <MenuItem key={b.currency} value={b.currency}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: "12px",
                  }}
                >
                  <span>{b.currency}</span>
                  <Typography
                    component="span"
                    sx={{ color: "text.secondary", fontSize: "13px" }}
                  >
                    {b.amount}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>

          <Typography
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginBottom: "6px",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            {t("p2p:amount", { defaultValue: "Сумма" })}
          </Typography>
          <TextField
            fullWidth
            type="text"
            placeholder="0.00"
            value={amount}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
              setAmount(v);
            }}
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
                fontSize: "22px",
                fontWeight: 600,
              },
            }}
            inputProps={{ inputMode: "decimal" }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "6px",
              fontSize: "12px",
              color: amountNum > balanceNum ? "error.main" : "text.secondary",
            }}
          >
            <span>
              {t("p2p:balance", { defaultValue: "Баланс" })}:{" "}
              {currentBalance?.amount ?? "0"} {currency}
            </span>
            <ButtonBase
              onClick={() => setAmount(currentBalance?.amount ?? "0")}
              sx={{ color: "primary.main", fontWeight: 500 }}
            >
              {t("p2p:max", { defaultValue: "Всё" })}
            </ButtonBase>
          </Box>

          <TextField
            fullWidth
            placeholder={t("p2p:comment_placeholder", {
              defaultValue: "Комментарий (необязательно)",
            })}
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 280))}
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
                marginTop: "16px",
              },
            }}
          />

          <Button
            onClick={() => setStep("pin")}
            disabled={!isAmountValid || !currency}
            sx={{ marginTop: "24px" }}
          >
            {t("p2p:next", { defaultValue: "Далее" })}
          </Button>
        </>
      )}

      {/* ---- Шаг 3: PIN ---- */}
      {step === "pin" && (
        <>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginBottom: "16px",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            {t("p2p:pin_hint", {
              defaultValue: "Введите PIN-код для подтверждения перевода.",
            })}
          </Typography>

          <Box
            sx={{
              textAlign: "center",
              fontSize: "24px",
              fontWeight: 700,
              color: "text.primary",
              marginBottom: "20px",
            }}
          >
            {amount} {currency}
            <Typography
              sx={{
                fontSize: "13px",
                color: "text.secondary",
                fontWeight: 400,
                marginTop: "4px",
              }}
            >
              → {recipient?.display_name}
            </Typography>
          </Box>

          <TextField
            autoFocus
            fullWidth
            placeholder="••••"
            type="password"
            value={pin}
            onChange={(e) =>
              setPin(e.target.value.replace(/\D/g, "").slice(0, 4))
            }
            InputProps={{
              sx: {
                borderRadius: "14px",
                bgcolor: "secondary.main",
                fontSize: "24px",
                letterSpacing: "14px",
                "& input": { textAlign: "center" },
              },
            }}
            inputProps={{ inputMode: "numeric", maxLength: 4 }}
          />

          <Button
            onClick={() => transferMut.mutate()}
            disabled={!isPinValid || transferMut.isLoading}
            sx={{ marginTop: "24px" }}
          >
            {transferMut.isLoading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              t("p2p:confirm_transfer", {
                defaultValue: "Подтвердить перевод",
              })
            )}
          </Button>
        </>
      )}

      {/* ---- Шаг 4: успех ---- */}
      {step === "done" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: "40px",
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "rgba(46, 204, 113, 0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <Icon
              icon="solar:check-circle-bold"
              width={48}
              height={48}
              color="#2ecc71"
            />
          </Box>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: "text.primary",
              marginBottom: "8px",
            }}
          >
            {amount} {currency}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "text.secondary",
              marginBottom: "40px",
            }}
          >
            {t("p2p:sent_to", { defaultValue: "Отправлено" })}{" "}
            {recipient?.display_name}
          </Typography>
          <Button onClick={onClose} sx={{ width: "100%" }}>
            {t("p2p:done", { defaultValue: "Готово" })}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

function Header({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "20px",
      }}
    >
      <IconButton
        onClick={onBack}
        sx={{ color: "text.primary" }}
        aria-label="close"
      >
        <Icon icon="solar:alt-arrow-left-linear" width={22} height={22} />
      </IconButton>
      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 600,
          color: "text.primary",
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function pickMessage(err: unknown): string {
  const raw =
    (err as { response?: { data?: { message?: string | string[] } } })?.response
      ?.data?.message;
  const msg = Array.isArray(raw) ? raw.join(", ") : raw;
  return msg || "Не удалось выполнить действие";
}
